import api from "./apiClient";
import API_ENDPOINTS from "../config/apiEndpoints";

const IPV4_PATTERN =
  /^(?:(?:25[0-5]|2[0-4]\d|1?\d?\d)\.){3}(?:25[0-5]|2[0-4]\d|1?\d?\d)$/;

export const isValidIpv4 = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed || trimmed.includes("/") || trimmed.includes(":")) return false;
  return IPV4_PATTERN.test(trimmed);
};

export const isValidIpv4Cidr = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed || trimmed.includes(":") || !trimmed.includes("/")) return false;

  const [ipPart, prefixPart] = trimmed.split("/");
  const prefix = Number(prefixPart);
  return (
    IPV4_PATTERN.test(String(ipPart || "").trim()) &&
    Number.isInteger(prefix) &&
    prefix >= 0 &&
    prefix <= 32
  );
};

export const isValidIpv4OrCidr = (value) =>
  isValidIpv4(value) || isValidIpv4Cidr(value) || isValidIpv4Range(value);

export const ipv4ToLong = (value) => {
  if (!isValidIpv4(value)) return null;
  const parts = value.split(".").map(Number);
  return (
    ((parts[0] << 24) >>> 0) +
    (parts[1] << 16) +
    (parts[2] << 8) +
    parts[3]
  );
};

export const buildIpv4Range = (startIp, endIp) => {
  const start = String(startIp || "").trim();
  const end = String(endIp || "").trim();
  if (!isValidIpv4(start) || !isValidIpv4(end)) return "";
  const startNum = ipv4ToLong(start);
  const endNum = ipv4ToLong(end);
  if (startNum == null || endNum == null || startNum > endNum) return "";
  return `${start}-${end}`;
};

export const isValidIpv4Range = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed || trimmed.includes("/") || !trimmed.includes("-")) return false;
  const parts = trimmed.split("-");
  if (parts.length !== 2) return false;
  return buildIpv4Range(parts[0].trim(), parts[1].trim()) !== "";
};

export const formatAllowlistIpDisplay = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "—";
  if (trimmed.includes("-")) {
    const [start, end] = trimmed.split("-");
    return `${start.trim()} – ${end.trim()}`;
  }
  return trimmed;
};

export const ipValueFromRow = (startIp, endIp) => {
  const start = String(startIp || "").trim();
  const end = String(endIp || "").trim();
  if (!isValidIpv4(start)) return "";
  const effectiveEnd = isValidIpv4(end) ? end : start;
  if (start === effectiveEnd) return start;
  return buildIpv4Range(start, effectiveEnd);
};

export const parseAllowlistEntryToRow = (entry) => {
  const value = String(entry?.IpValue || "").trim();
  let startIp = "";
  let endIp = "";

  if (value.includes("-")) {
    const parts = value.split("-");
    startIp = parts[0]?.trim() || "";
    endIp = parts[1]?.trim() || "";
  } else {
    startIp = value;
    endIp = value;
  }

  return {
    rowKey: entry.Id,
    serverId: entry.Id,
    label: entry.Label || "",
    startIp,
    endIp,
  savedIpValue: value,
  savedLabel: entry.Label || "",
  isDraft: false,
    isSaving: false,
    error: "",
  };
};

export const buildIpv4Cidr = (network, prefix) => {
  const net = String(network || "").trim();
  const p = Number(prefix);
  if (!isValidIpv4(net) || !Number.isInteger(p) || p < 0 || p > 32) return "";
  return `${net}/${p}`;
};

const isLoopbackOrMissingIpv4 = (ip) => {
  const trimmed = String(ip || "").trim();
  return !trimmed || trimmed.includes(":") || trimmed === "127.0.0.1";
};

const fetchPublicIpv4 = async () => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 5000);
  try {
    const res = await fetch("https://api.ipify.org?format=json", {
      method: "GET",
      signal: controller.signal,
    });
    const data = await res.json();
    const ip = String(data?.ip || "").trim();
    return IPV4_PATTERN.test(ip) ? ip : "";
  } catch {
    return "";
  } finally {
    clearTimeout(timeoutId);
  }
};

/** Public IPv4 lookup for login (no auth). */
export const getPublicClientIpv4 = fetchPublicIpv4;

export const fetchOrganizationLoginIps = async (organizationId) => {
  return api.get(
    `${API_ENDPOINTS.ORG_SECURITY.GET_LOGIN_IPS}?organizationId=${encodeURIComponent(organizationId)}`,
  );
};

export const addOrganizationLoginIp = async (payload) => {
  return api.post(API_ENDPOINTS.ORG_SECURITY.LOGIN_IP_ACTIVITY, {
    ActionType: "Add",
    ...payload,
  });
};

export const removeOrganizationLoginIp = async (payload) => {
  return api.post(API_ENDPOINTS.ORG_SECURITY.LOGIN_IP_ACTIVITY, {
    ActionType: "Remove",
    ...payload,
  });
};

export const fetchSetupClientIp = async () => {
  const response = await api.get(API_ENDPOINTS.ORG_SECURITY.GET_SETUP_CLIENT_IP);
  return response?.data?.ClientIp || "";
};

/** Resolves a usable IPv4 for "Add my IP" — API first, then public IP lookup on localhost. */
export const fetchClientIpv4 = async () => {
  try {
    const apiIp = await fetchSetupClientIp();
    if (!isLoopbackOrMissingIpv4(apiIp) && IPV4_PATTERN.test(apiIp)) {
      return apiIp;
    }
  } catch {
    // fall through to public lookup
  }

  return fetchPublicIpv4();
};

/** Default rule name when adding the current client IPv4 (e.g. Rule_20260624_143052). */
export const buildAutoAllowlistRuleName = () => {
  const now = new Date();
  const pad = (value) => String(value).padStart(2, "0");
  const date = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
  const time = `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  return `Rule_${date}_${time}`;
};

const GENERIC_ALLOWLIST_MESSAGES = new Set([
  "",
  "ok",
  "ip removed.",
  "ip removed from allowlist.",
  "ip removed from user allowlist.",
]);

export const isGenericAllowlistApiMessage = (message) =>
  GENERIC_ALLOWLIST_MESSAGES.has(String(message || "").trim().toLowerCase());

export const buildAllowlistRemoveSuccessMessage = (row, { scope = "organization" } = {}) => {
  const label = String(row?.label || "").trim();
  const ipDisplay = formatAllowlistIpDisplay(
    ipValueFromRow(row?.startIp, row?.endIp) || row?.savedIpValue || "",
  );
  const scopeLabel = scope === "staff" ? "user allowlist" : "organisation allowlist";

  if (label && ipDisplay !== "—") {
    return `The rule "${label}" (${ipDisplay}) has been removed from the ${scopeLabel}.`;
  }
  if (ipDisplay !== "—") {
    return `The IP rule ${ipDisplay} has been removed from the ${scopeLabel}.`;
  }
  return `The allowlist rule has been removed from the ${scopeLabel}.`;
};
